import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, auth } from '../firebase';
import {
  collection, query, where, onSnapshot, doc, getDoc, setDoc,
  deleteDoc, getDocs, writeBatch, serverTimestamp, limit, addDoc
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { Html5Qrcode } from 'html5-qrcode';
import {
  FiArrowLeft, FiSearch, FiUserPlus, FiUserCheck, FiCheck,
  FiX, FiUser, FiUsers, FiClock, FiShare2
} from 'react-icons/fi';
import { MdQrCodeScanner } from 'react-icons/md';

const Friendlist = () => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchBox, setShowSearchBox] = useState(false);

  // --- BARÁT QR SCANNER MODAL ÁLLAPOTOK ---
  const [showQrScannerModal, setShowQrScannerModal] = useState(false);
  const html5QrCodeRef = useRef(null);

  const [friendsList, setFriendsList] = useState([]);
  const [friendRequests, setFriendRequests] = useState([]);
  const [sentRequestIds, setSentRequestIds] = useState(new Set());
  const [friendToDelete, setFriendToDelete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 1. Auth állapot és profil
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, "users", user.uid));
          if (userDoc.exists()) {
            setUserProfile(userDoc.data());
          }
        } catch (e) {
          console.error("Hiba a profil betöltésekor:", e);
        }
      } else {
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  // 2. Barátok listájának valós idejű betöltése
  useEffect(() => {
    if (!currentUser) return;

    const friendsRef = collection(db, "friends", currentUser.uid, "userFriends");
    const unsubscribeFriends = onSnapshot(friendsRef, async (snapshot) => {
      const list = [];
      for (const docSnap of snapshot.docs) {
        const friendUid = docSnap.id;
        const data = docSnap.data();
        try {
          const uDoc = await getDoc(doc(db, "users", friendUid));
          if (uDoc.exists()) {
            const uData = uDoc.data();
            list.push({
              uid: friendUid,
              name: `${uData.lastName || ''} ${uData.firstName || ''}`.trim() || uData.displayName || data.name || 'Íjász',
              club: uData.clubName || uData.club || data.club || '',
              imageUrl: uData.profileImageUrl || data.profileImageUrl || null
            });
          } else {
            list.push({
              uid: friendUid,
              name: data.name || 'Íjász',
              club: data.club || '',
              imageUrl: data.profileImageUrl || null
            });
          }
        } catch (e) {
          list.push({ uid: friendUid, name: data.name || 'Íjász', club: '', imageUrl: null });
        }
      }
      setFriendsList(list);
      setLoading(false);
    });

    return () => unsubscribeFriends();
  }, [currentUser]);

  // 3. Bejövő Barátkérelmek valós idejű betöltése
  useEffect(() => {
    if (!currentUser) return;

    const requestsRef = collection(db, "friends", currentUser.uid, "friendRequests");
    const unsubscribeRequests = onSnapshot(requestsRef, async (snapshot) => {
      const requests = [];
      for (const docSnap of snapshot.docs) {
        const senderUid = docSnap.id;
        const data = docSnap.data();
        try {
          const uDoc = await getDoc(doc(db, "users", senderUid));
          if (uDoc.exists()) {
            const uData = uDoc.data();
            requests.push({
              uid: senderUid,
              name: `${uData.lastName || ''} ${uData.firstName || ''}`.trim() || data.fromUserName || 'Íjász',
              club: uData.clubName || uData.club || data.fromUserClub || '',
              imageUrl: uData.profileImageUrl || data.fromUserImage || null
            });
          } else {
            requests.push({
              uid: senderUid,
              name: data.fromUserName || 'Íjász',
              club: data.fromUserClub || '',
              imageUrl: data.fromUserImage || null
            });
          }
        } catch (e) {
          requests.push({ uid: senderUid, name: data.fromUserName || 'Íjász', club: '', imageUrl: null });
        }
      }
      setFriendRequests(requests);
    });

    return () => unsubscribeRequests();
  }, [currentUser]);

  // --- XOR TITKOSÍTÁS FELOLDÁSA (PONTOSAN AZ ANROID APP KÓDJA ALAPJÁN) ---
  const xorCipher = (data) => {
    const key = "ARCHERY_NEXUS_2024_SAFE_KEY";
    let output = "";
    for (let i = 0; i < data.length; i++) {
      output += String.fromCharCode(data.charCodeAt(i) ^ key.charCodeAt(i % key.length));
    }
    return output;
  };

  const decodeProfileQr = (qrRawData) => {
    if (!qrRawData) return null;
    const cleanRaw = qrRawData.trim();

    try {
      // 1. Base64 dekódolás
      const cipheredText = atob(cleanRaw);

      // 2. XOR feloldása
      const decryptedData = xorCipher(cipheredText);

      // 3. Split (ANX|UID|NAME)
      const parts = decryptedData.split("|");
      if (parts.length >= 2 && parts[0] === "ANX") {
        return parts[1].trim(); // Valódi 28-karakteres Firebase UID
      }
    } catch (e) {
      // Fallback ha nem Base64/XOR titkosított
    }

    if (cleanRaw.includes(';') || cleanRaw.includes(':') || cleanRaw.includes('|')) {
      const parts = cleanRaw.split(/[:;|]/);
      if (parts.length >= 2 && parts[0] === 'ANX') {
        return parts[1].trim();
      }
    }

    return cleanRaw;
  };

  // --- KAMERA KEZELÉSE A BARÁT QR SCANNER MODALBAN ---
  useEffect(() => {
    if (!showQrScannerModal) {
      if (html5QrCodeRef.current) {
        try { html5QrCodeRef.current.stop(); } catch (e) {}
        html5QrCodeRef.current = null;
      }
      return;
    }

    const startScanner = async () => {
      try {
        const scanner = new Html5Qrcode("friend-qr-reader");
        html5QrCodeRef.current = scanner;

        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 220, height: 220 } },
          (decodedText) => {
            if (decodedText) {
              try { scanner.stop(); } catch (e) {}
              html5QrCodeRef.current = null;

              // ANX PROFIL QR TITKOSÍTÁS FELOLDÁSA
              const scannedUid = decodeProfileQr(decodedText);
              if (scannedUid) {
                handleSendFriendRequestById(scannedUid);
              }
            }
          },
          () => {}
        );
      } catch (e) {
        console.error("Kamera indítási hiba:", e);
      }
    };

    const timer = setTimeout(startScanner, 300);
    return () => {
      clearTimeout(timer);
      if (html5QrCodeRef.current) {
        try { html5QrCodeRef.current.stop(); } catch (e) {}
        html5QrCodeRef.current = null;
      }
    };
  }, [showQrScannerModal]);

  // --- BARÁTFELKÉRÉS KÜLDÉSE QR ID ALAPJÁN ---
  const handleSendFriendRequestById = async (scannedFriendId) => {
    if (!currentUser) return;

    if (scannedFriendId === currentUser.uid) {
      alert("Saját magadnak nem küldhetsz felkérést!");
      return;
    }

    try {
      const myName = userProfile
        ? `${userProfile.lastName || ''} ${userProfile.firstName || ''}`.trim()
        : (currentUser.displayName || 'Íjász');
      const myClub = userProfile?.clubName || userProfile?.club || '';
      const myImage = userProfile?.profileImageUrl || currentUser.photoURL || null;

      // 1. BEJÖVŐ KÉRELEM LÉTREHOZÁSA A CÉL-ÍJÁSZ NÁL
      const requestRef = doc(db, "friends", scannedFriendId, "friendRequests", currentUser.uid);
      await setDoc(requestRef, {
        status: "pending",
        fromUserId: currentUser.uid,
        fromUserName: myName,
        fromUserClub: myClub,
        fromUserImage: myImage,
        createdAt: serverTimestamp()
      });

      // 2. KÜLDÖTT KÉRELEM LÉTREHOZÁSA NÁLUNK
      const sentRef = doc(db, "friends", currentUser.uid, "sentRequests", scannedFriendId);
      await setDoc(sentRef, {
        status: "pending",
        sentAt: serverTimestamp()
      });

      // 3. ÉRTE SÍTÉS KÜLDÉSE
      await addDoc(collection(db, "notifications"), {
        toUserId: scannedFriendId,
        fromUserId: currentUser.uid,
        fromUserName: myName,
        type: "friend_request",
        status: "unread",
        createdAt: serverTimestamp()
      });

      setShowQrScannerModal(false);
      alert("Barátfelkérés sikeresen elküldve! 🏹🤝");
    } catch (e) {
      console.error("Hiba a felkérés küldésekor:", e);
      alert("Hiba történt a felkérés küldése során.");
    }
  };

  // --- MEGHÍVÓ LINK MEGOSZTÁSA ---
  const handleShareInviteLink = async () => {
    const inviteUrl = "https://www.archerynexus.com";
    const shareData = {
      title: "ArcheryNexus",
      text: "Szia! Csatlakozz te is az ArcheryNexus íjász közösséghez!",
      url: inviteUrl
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (e) {
        console.warn(e);
      }
    } else {
      navigator.clipboard.writeText(inviteUrl);
      alert("Meghívó hivatkozás másolva a vágólapra! 🏹");
    }
  };

  // 2. Barátkérelem elfogadása
  const handleAcceptRequest = async (requestUser) => {
    if (!currentUser) return;

    try {
      const batch = writeBatch(db);

      const myName = userProfile
        ? `${userProfile.lastName || ''} ${userProfile.firstName || ''}`.trim()
        : (currentUser.displayName || 'Íjász');
      const myClub = userProfile?.clubName || userProfile?.club || '';
      const myImage = userProfile?.profileImageUrl || currentUser.photoURL || null;

      const myFriendRef = doc(db, "friends", currentUser.uid, "userFriends", requestUser.uid);
      batch.set(myFriendRef, {
        name: requestUser.name,
        club: requestUser.club,
        profileImageUrl: requestUser.imageUrl,
        createdAt: serverTimestamp()
      });

      const targetFriendRef = doc(db, "friends", requestUser.uid, "userFriends", currentUser.uid);
      batch.set(targetFriendRef, {
        name: myName,
        club: myClub,
        profileImageUrl: myImage,
        createdAt: serverTimestamp()
      });

      const requestRef = doc(db, "friends", currentUser.uid, "friendRequests", requestUser.uid);
      batch.delete(requestRef);

      await batch.commit();

      alert(`Mostantól barátok vagytok vele: ${requestUser.name}! 🏹🤝`);
    } catch (e) {
      console.error("Hiba az elfogadáskor:", e);
      alert("Hiba történt a barátkérelem elfogadásakor.");
    }
  };

  // 3. Barátkérelem elutasítása
  const handleDeclineRequest = async (requestUser) => {
    if (!currentUser) return;

    try {
      const requestRef = doc(db, "friends", currentUser.uid, "friendRequests", requestUser.uid);
      await deleteDoc(requestRef);
    } catch (e) {
      console.error("Hiba az elutasításkor:", e);
    }
  };

  return (
    <div style={{ ...pageStyle, padding: isMobile ? '12px 10px 80px 10px' : '20px 20px 80px 20px' }} className="force-light-theme">
      {/* FEJLÉC */}
      <div style={{ ...headerStyle, justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button style={backButtonStyle} onClick={() => navigate('/kozosseg')}>
            <FiArrowLeft size={22} />
          </button>
          <h2 style={{ margin: 0, color: '#b30000', fontSize: '1.3rem', fontWeight: 'bold' }}>Barátok</h2>
        </div>

        {/* 2 SÖTÉTSZÜRKE LEKEREKÍTETT IKONGOMB JOBBRA */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {/* 🔍 NAGYÍTÓ IKON (KERESÉS A MEGLÉVŐ BARÁTOK KÖZÖTT) */}
          <button
            type="button"
            onClick={() => setShowSearchBox(!showSearchBox)}
            style={{
              backgroundColor: showSearchBox ? '#b30000' : '#282828',
              color: '#ffffff',
              border: 'none',
              borderRadius: '10px',
              padding: '8px 10px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
              transition: 'all 0.2s ease'
            }}
            title="Keresés a meglévő barátok között"
          >
            <FiSearch size={20} color="#ffffff" />
          </button>

          {/* 👤+ EMBERKE ÉS PLUSZ IKON (QR-KÓDOS BARÁT HOZZÁADÁSA MODAL) */}
          <button
            type="button"
            onClick={() => setShowQrScannerModal(true)}
            style={{
              backgroundColor: '#282828',
              color: '#ffffff',
              border: 'none',
              borderRadius: '10px',
              padding: '8px 10px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
              transition: 'all 0.2s ease'
            }}
            title="Barát hozzáadása QR-kóddal"
          >
            <FiUserPlus size={20} color="#ffffff" />
          </button>
        </div>
      </div>

      <div style={{ maxWidth: '600px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '20px' }}>

        {/* KERESŐ MEZŐ (NAGYÍTÓ GOMBRA RÁKATTINTVA) */}
        {showSearchBox && (
          <div style={cardStyle}>
            <div style={{ position: 'relative' }}>
              <FiSearch style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#888' }} size={18} />
              <input
                type="text"
                placeholder="Keresés a barátok között név vagy egyesület alapján..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={searchInputStyle}
                autoFocus
              />
            </div>
          </div>
        )}

        {/* BEJÖVŐ BARÁTKERELMEK */}
        {friendRequests.length > 0 && (
          <div style={{ ...cardStyle, border: '1px solid #feb2b2', backgroundColor: '#fff8f8' }}>
            <h4 style={{ margin: '0 0 12px 0', color: '#b30000', fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>📩 Bejövő barátkérelmek</span>
              <span style={requestBadgeCountStyle}>{friendRequests.length}</span>
            </h4>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {friendRequests.map(request => (
                <div key={request.uid} style={{ ...userCardStyle, backgroundColor: '#fff' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={avatarStyle}>
                      {request.imageUrl ? (
                        <img src={request.imageUrl} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                      ) : (
                        <FiUser size={18} color="#888" />
                      )}
                    </div>
                    <div>
                      <div style={{ fontWeight: 'bold', fontSize: '0.9rem', color: '#333' }}>{request.name}</div>
                      <div style={{ fontSize: '0.75rem', color: '#666' }}>{request.club || 'Egyéni induló'}</div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      type="button"
                      onClick={() => handleAcceptRequest(request)}
                      style={acceptButtonStyle}
                    >
                      <FiCheck size={14} /> Elfogadás
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeclineRequest(request)}
                      style={declineButtonStyle}
                    >
                      <FiX size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* MEGLÉVŐ BARÁTOK LISTÁJA */}
        <div style={cardStyle}>
          <h4 style={{ margin: '0 0 15px 0', color: '#333', fontSize: '0.95rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>👥 Barátaim</span>
            <span style={{ fontSize: '0.8rem', color: '#888', fontWeight: 'bold' }}>{friendsList.length} fő</span>
          </h4>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#b30000', fontWeight: 'bold' }}>Barátok betöltése...</div>
          ) : friendsList.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '25px', color: '#888', fontStyle: 'italic', fontSize: '0.85rem', lineHeight: '1.5' }}>
              Még nincsenek barátaid.<br/>Adj hozzá valakit a profilján található QR-kóddal! 🏹🤝
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {friendsList
                .filter(f => {
                  if (!showSearchBox || !searchTerm.trim()) return true;
                  const term = searchTerm.toLowerCase();
                  return (f.name || '').toLowerCase().includes(term) || (f.club || '').toLowerCase().includes(term);
                })
                .map(friend => (
                  <div key={friend.uid} style={userCardStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={avatarStyle}>
                        {friend.imageUrl ? (
                          <img src={friend.imageUrl} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                        ) : (
                          <FiUser size={18} color="#888" />
                        )}
                      </div>
                      <div>
                        <div style={{ fontWeight: 'bold', fontSize: '0.9rem', color: '#333' }}>{friend.name}</div>
                        <div style={{ fontSize: '0.75rem', color: '#666' }}>{friend.club || 'Egyéni induló'}</div>
                      </div>
                    </div>

                    <span
                      style={{ ...alreadyFriendBadgeStyle, cursor: 'pointer' }}
                      onClick={() => setFriendToDelete(friend)}
                      title="Kattints a barát törléséhez"
                    >
                      <FiUserCheck size={14} /> Barátod
                    </span>
                  </div>
                ))
              }
            </div>
          )}
        </div>

      </div>

      {/* --- BARÁT TÖRLÉSE MEGERŐSÍTŐ MODAL --- */}
      {friendToDelete && (
        <div style={modalOverlayStyle}>
          <div style={{ ...modalContentStyle, maxWidth: '400px', textAlign: 'center', padding: '24px' }}>
            <h3 style={{ margin: '0 0 10px 0', color: '#b30000', fontSize: '1.2rem', fontWeight: 'bold' }}>
              Barát törlése
            </h3>
            <p style={{ color: '#666', marginBottom: '25px', lineHeight: '1.5', fontSize: '0.9rem' }}>
              Biztosan törölni szeretnéd ezt a barátot? <br/>
              <strong>{friendToDelete.name}</strong>
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                type="button"
                onClick={() => setFriendToDelete(null)}
                style={cancelButtonStyle}
              >
                Mégse
              </button>
              <button
                type="button"
                onClick={async () => {
                  try {
                    const batch = writeBatch(db);
                    const myFriendRef = doc(db, "friends", currentUser.uid, "userFriends", friendToDelete.uid);
                    const theirFriendRef = doc(db, "friends", friendToDelete.uid, "userFriends", currentUser.uid);

                    batch.delete(myFriendRef);
                    batch.delete(theirFriendRef);
                    await batch.commit();

                    setFriendToDelete(null);
                    alert("Barát eltávolítva! 🗑️");
                  } catch (e) {
                    console.error("Hiba a barát törlésekor:", e);
                    alert("Hiba történt a törlés során.");
                  }
                }}
                style={deleteConfirmButtonStyle}
              >
                Igen, törlöm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- BARÁT HOZZÁADÁSA QR KAMERA MODAL (AZ ANROID APP layout_qr_scanner_bottom_sheet.xml MINTÁJÁRA) --- */}
      {showQrScannerModal && (
        <div style={modalOverlayStyle}>
          <div style={{ ...modalContentStyle, maxWidth: '400px', textAlign: 'center', padding: '24px' }}>
            {/* BEZÁRÁS X GOMB */}
            <div
              style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '5px', cursor: 'pointer' }}
              onClick={() => setShowQrScannerModal(false)}
            >
              <FiX size={24} color="#888" />
            </div>

            {/* FOGANTYÚ VIZUÁLIS ELEM */}
            <div style={{ width: '40px', height: '4px', backgroundColor: '#CCCCCC', borderRadius: '2px', margin: '0 auto 16px auto' }} />

            {/* PIROS FŐCÍM */}
            <h3 style={{ margin: '0 0 20px 0', color: '#D32F2F', fontSize: '1.3rem', fontWeight: 'bold' }}>
              Barát hozzáadása
            </h3>

            {/* ÉLŐ KAMERA KERET (280x280px, 20px LEKEREKÍTÉS) */}
            <div style={{
              width: '260px',
              height: '260px',
              margin: '0 auto',
              borderRadius: '20px',
              overflow: 'hidden',
              boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
              backgroundColor: '#000',
              position: 'relative'
            }}>
              <div id="friend-qr-reader" style={{ width: '100%', height: '100%' }} />
            </div>

            {/* PIROS ALCÍM A KAMERA ALATT */}
            <div style={{ color: '#D32F2F', fontWeight: 'bold', fontSize: '0.95rem', marginTop: '16px' }}>
              Olvasd be a barátod QR kódját
            </div>

            {/* VIZUÁLIS ELVÁLASZTÓ "VAGY" FELIRATTAL */}
            <div style={{ display: 'flex', alignItems: 'center', margin: '24px 0 16px 0' }}>
              <div style={{ flex: 1, height: '1px', backgroundColor: '#DDDDDD' }} />
              <span style={{ color: '#999999', fontSize: '0.75rem', fontWeight: 'bold', margin: '0 16px' }}>VAGY</span>
              <div style={{ flex: 1, height: '1px', backgroundColor: '#DDDDDD' }} />
            </div>

            {/* MEGHÍVÓ SZEKCIÓ */}
            <div style={{ color: '#666', fontSize: '0.9rem', marginBottom: '12px' }}>
              Nincs még itt a barátod?
            </div>

            <button
              type="button"
              onClick={handleShareInviteLink}
              style={inviteButtonStyle}
            >
              <FiShare2 size={18} />
              <span>Meghívó küldése</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// --- STÍLUSOK ---
const pageStyle = {
  backgroundColor: '#f4f6f8',
  minHeight: '100vh',
  boxSizing: 'border-box'
};

const headerStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '20px',
  paddingBottom: '12px',
  borderBottom: '1px solid #e0e0e0',
  maxWidth: '600px',
  margin: '0 auto 20px auto'
};

const backButtonStyle = {
  background: 'none',
  border: 'none',
  color: '#333',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  padding: '6px'
};

const cardStyle = {
  backgroundColor: 'white',
  borderRadius: '12px',
  padding: '16px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  border: '1px solid #eee'
};

const searchInputStyle = {
  width: '100%',
  padding: '10px 12px 10px 38px',
  borderRadius: '8px',
  border: '1px solid #ddd',
  fontSize: '0.85rem',
  boxSizing: 'border-box',
  outline: 'none'
};

const userCardStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '10px 12px',
  borderRadius: '8px',
  border: '1px solid #eee',
  backgroundColor: '#fafafa'
};

const avatarStyle = {
  width: '36px',
  height: '36px',
  borderRadius: '50%',
  backgroundColor: '#eee',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  overflow: 'hidden'
};

const acceptButtonStyle = {
  padding: '6px 12px',
  backgroundColor: '#28a745',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  fontWeight: 'bold',
  fontSize: '0.8rem',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '4px'
};

const declineButtonStyle = {
  padding: '6px 8px',
  backgroundColor: '#eee',
  color: '#666',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const alreadyFriendBadgeStyle = {
  backgroundColor: '#e6fffa',
  color: '#28a745',
  border: '1px solid #b2f5ea',
  padding: '4px 10px',
  borderRadius: '12px',
  fontSize: '0.75rem',
  fontWeight: 'bold',
  display: 'flex',
  alignItems: 'center',
  gap: '4px'
};

const requestBadgeCountStyle = {
  backgroundColor: '#D32F2F',
  color: 'white',
  borderRadius: '50%',
  width: '20px',
  height: '20px',
  fontSize: '0.75rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 'bold'
};

const modalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.6)',
  backdropFilter: 'blur(3px)',
  zIndex: 10005,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '15px'
};

const modalContentStyle = {
  backgroundColor: 'white',
  borderRadius: '20px',
  boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
  width: '100%',
  maxHeight: '90vh',
  overflowY: 'auto'
};

const cancelButtonStyle = {
  flex: 1,
  padding: '10px',
  border: 'none',
  backgroundColor: '#eee',
  color: '#555',
  borderRadius: '8px',
  fontWeight: 'bold',
  cursor: 'pointer'
};

const deleteConfirmButtonStyle = {
  flex: 1,
  padding: '10px',
  border: 'none',
  backgroundColor: '#b30000',
  color: 'white',
  borderRadius: '8px',
  fontWeight: 'bold',
  cursor: 'pointer'
};

const inviteButtonStyle = {
  padding: '10px 20px',
  backgroundColor: '#D32F2F',
  color: 'white',
  border: 'none',
  borderRadius: '12px',
  fontWeight: 'bold',
  fontSize: '0.9rem',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  boxShadow: '0 4px 10px rgba(211,47,47,0.3)'
};

export default Friendlist;
